import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { useWindowSize } from "@reactuses/core";
import { ReactNode, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  AnalysisFilter,
  AnalysisVehicleRow,
  ListQuery,
  ListResult,
  getAnalysisDeviceList,
  getAnalysisEventList,
  getAnalysisTripList,
  getAnalysisVehicleList,
} from "../../api/analysis";
import { readShopZoneError } from "../../api/shopZone";
import { ModalLayout } from "../../components/common/ModalLayout";
import theme from "../../theme";
import { EVENT_TYPES, fmt1, fmtInt, fmtMinutes, n, per10kHours } from "./analysisUtil";

/**
 * 빅데이터 분석 상세 목록 — 요약 KPI·차트·처리 결과 항목을 눌렀을 때 뜬다.
 * 탭마다 이벤트·운행·차량별·단말 목록 중 하나를 보여 준다. 정렬·검색·엑셀이 된다.
 * 건수는 화면 집계와 같은 SQL 조건으로 센다 (서버 매퍼 eventListWhere·operListWhere).
 */

const { md } = theme.screen;

export type DetailTab =
  | { kind: "events"; label: string; query: ListQuery }
  | { kind: "trips"; label: string; query: ListQuery }
  | {
      kind: "vehicles";
      label: string;
      query: ListQuery;
      /** 처음 정렬할 열 (큰 값부터) */
      sortBy?: "trips" | "hours" | "km" | "rollover" | "danger" | "area";
      /** 운행이 있는 차량만 (운행 대수·시간·거리 항목) */
      onlyTrips?: boolean;
    }
  | { kind: "devices"; label: string };

export interface DetailSpec {
  title: string;
  color?: string;
  /** 제목 아래 한 줄 설명 */
  note?: string;
  tabs: DetailTab[];
}

type Row = Record<string, unknown>;
interface Col {
  key: string;
  label: string;
  /** 정렬·검색·엑셀에 쓰는 값 */
  value: (r: Row) => string | number | null;
  /** 표에 그릴 것 (없으면 value) */
  render?: (r: Row) => ReactNode;
  align?: "l" | "r" | "c";
  /** true: 긴 글(사유·주소)은 줄바꿈, "s": 이름류는 짧게 줄바꿈 */
  wrap?: boolean | "s";
}

const STATUS_LABEL: Record<number, string> = { 0: "미처리", 1: "취소", 2: "해제" };
const FLAG_LABEL: Record<string, string> = { open: "종료 없음", odd: "시각 이상", over: "24시간 초과", ok: "" };
const ACTIVE_LABEL: Record<string, string> = { "7d": "최근 7일 수신", "30d": "8~30일 전 수신", none: "30일 넘게 없음" };
const ACTIVE_COLOR: Record<string, string> = { "7d": "#25863F", "30d": "#d97706", none: "#9ca3af" };

const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));
const numOrNull = (v: unknown) => (v === null || v === undefined || v === "" ? null : Number(v));
/** 'yyyy-MM-dd HH:mm:ss' 를 날짜·시각 두 줄로 (열 폭을 줄인다) */
const twoLine = (v: unknown) => {
  const t = str(v);
  if (!t) return "-";
  const i = t.indexOf(" ");
  if (i < 0) return t;
  return (
    <span className="dt2">
      {t.slice(0, i)}
      <br />
      <span className="tm">{t.slice(i + 1)}</span>
    </span>
  );
};

const hms = (secs: unknown) => {
  const s = numOrNull(secs);
  if (s === null || !Number.isFinite(s)) return "-";
  const neg = s < 0;
  const a = Math.abs(s);
  const h = Math.floor(a / 3600);
  const m = Math.floor((a % 3600) / 60);
  const sec = Math.floor(a % 60);
  return `${neg ? "-" : ""}${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

function columns(kind: DetailTab["kind"], typeName: (c: number | null) => string): Col[] {
  const vehicleCols: Col[] = [
    { key: "vhcl_nm", label: "차량", value: (r) => str(r.vhcl_nm), align: "l", wrap: "s" },
    { key: "type_nm", label: "차종", value: (r) => typeName(numOrNull(r.type_cd)), align: "l" },
    { key: "dvc_sn", label: "단말", value: (r) => str(r.dvc_sn) },
    { key: "ogdp_nm", label: "소속", value: (r) => str(r.ogdp_nm), align: "l", wrap: "s" },
  ];
  switch (kind) {
    case "events":
      return [
        { key: "dt", label: "발생 일시", value: (r) => str(r.dt), render: (r) => twoLine(r.dt) },
        {
          key: "type",
          label: "유형",
          value: (r) => EVENT_TYPES[str(r.type)]?.label ?? str(r.type),
          render: (r) => <span style={{ color: EVENT_TYPES[str(r.type)]?.color }}>{EVENT_TYPES[str(r.type)]?.label ?? str(r.type)}</span>,
        },
        ...vehicleCols,
        { key: "speed", label: "속도(km/h)", value: (r) => numOrNull(r.speed), align: "r" },
        { key: "status", label: "처리", value: (r) => (r.status === null ? "" : STATUS_LABEL[Number(r.status)] ?? str(r.status)) },
        { key: "rmv_rsn", label: "해제 사유", value: (r) => str(r.rmv_rsn), align: "l", wrap: true },
        { key: "rmv_dt", label: "해제 일시", value: (r) => str(r.rmv_dt), render: (r) => twoLine(r.rmv_dt) },
        {
          key: "handle_min",
          label: "처리 시간(분)",
          value: (r) => numOrNull(r.handle_min),
          // 0분은 1분 안에 해제한 것이다 (fmtMinutes 는 0 을 '-' 로 그린다)
          render: (r) => {
            const m = numOrNull(r.handle_min);
            return m === null ? "-" : m < 1 ? "1분 미만" : fmtMinutes(m);
          },
          align: "r",
        },
        { key: "addr", label: "주소", value: (r) => str(r.addr), align: "l", wrap: true },
      ];
    case "trips":
      return [
        { key: "start_dt", label: "시작", value: (r) => str(r.start_dt), render: (r) => twoLine(r.start_dt) },
        { key: "end_dt", label: "종료", value: (r) => str(r.end_dt), render: (r) => twoLine(r.end_dt) },
        { key: "secs", label: "운행시간(초)", value: (r) => numOrNull(r.secs), render: (r) => hms(r.secs), align: "r" },
        { key: "km", label: "이동거리(km)", value: (r) => numOrNull(r.km), align: "r" },
        ...vehicleCols,
        {
          key: "flag",
          label: "비고",
          value: (r) => FLAG_LABEL[str(r.flag)] ?? str(r.flag),
          render: (r) => <span className="warn">{FLAG_LABEL[str(r.flag)] ?? str(r.flag)}</span>,
        },
      ];
    case "vehicles":
      return [
        ...vehicleCols,
        { key: "trips", label: "운행 건수", value: (r) => n(r.trips as number), render: (r) => fmtInt(r.trips as number), align: "r" },
        { key: "hours", label: "운행시간(h)", value: (r) => n(r.hours as number), render: (r) => fmt1(r.hours as number), align: "r" },
        { key: "km", label: "이동거리(km)", value: (r) => n(r.km as number), render: (r) => fmt1(r.km as number), align: "r" },
        { key: "last_trip", label: "마지막 운행", value: (r) => str(r.last_trip), render: (r) => twoLine(r.last_trip) },
        { key: "rollover", label: "전복", value: (r) => n(r.rollover as number), align: "r" },
        { key: "danger", label: "위험운전", value: (r) => n(r.danger as number), align: "r" },
        { key: "area", label: "이탈·위험지역", value: (r) => n(r.area as number), align: "r" },
        {
          key: "rate",
          label: "1만 시간당 전복",
          value: (r) => (n(r.hours as number) > 0 ? Number(per10kHours(r.rollover as number, r.hours as number).toFixed(1)) : null),
          render: (r) => (n(r.hours as number) > 0 ? fmt1(per10kHours(r.rollover as number, r.hours as number)) : "-"),
          align: "r",
        },
        { key: "rollover_false", label: "전복 오탐", value: (r) => n(r.rollover_false as number), align: "r" },
      ];
    case "devices":
      return [
        ...vehicleCols,
        { key: "last_rcv", label: "마지막 수신(30일 이내)", value: (r) => str(r.last_rcv), render: (r) => twoLine(r.last_rcv) },
        {
          key: "active",
          label: "상태",
          value: (r) => ACTIVE_LABEL[str(r.active)] ?? str(r.active),
          render: (r) => <span style={{ color: ACTIVE_COLOR[str(r.active)] }}>{ACTIVE_LABEL[str(r.active)] ?? str(r.active)}</span>,
        },
      ];
  }
}

/** 표는 열 정의로 그리므로 행은 이름-값 묶음으로 다룬다 */
const asRows = <T,>(p: Promise<ListResult<T>>) => p as unknown as Promise<ListResult<Row>>;

function fetchTab(filter: AnalysisFilter, tab: DetailTab): Promise<ListResult<Row>> {
  switch (tab.kind) {
    case "events":
      return asRows(getAnalysisEventList(filter, tab.query));
    case "trips":
      return asRows(getAnalysisTripList(filter, tab.query));
    case "vehicles":
      return asRows(getAnalysisVehicleList(filter, tab.query));
    case "devices":
      return asRows(getAnalysisDeviceList(filter));
  }
}

function TabBody({
  filter,
  tab,
  title,
  typeName,
  onOpenRaw,
}: {
  filter: AnalysisFilter;
  tab: DetailTab;
  title: string;
  typeName: (c: number | null) => string;
  onOpenRaw: (id: number) => void;
}) {
  const [q, setQ] = useState("");
  const initialSort = tab.kind === "vehicles" ? tab.sortBy ?? "trips" : null;
  const [sort, setSort] = useState<{ key: string; desc: boolean } | null>(initialSort ? { key: initialSort, desc: true } : null);
  const cols = useMemo(() => columns(tab.kind, typeName), [tab.kind, typeName]);

  const list = useQuery({
    queryKey: ["analysis", "detail", tab.kind, filter, "query" in tab ? tab.query : null],
    queryFn: () => fetchTab(filter, tab),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // 운행 대수 등: 운행이 있는 차량만. 차량 미연결 운행 묶음은 남겨 합계를 맞춘다
  const base = useMemo(() => {
    const rows = list.data?.rows ?? [];
    if (tab.kind === "vehicles" && tab.onlyTrips) return rows.filter((r) => n(r.trips as number) > 0);
    return rows;
  }, [list.data, tab]);

  const rows = useMemo(() => {
    const k = q.trim().toLowerCase();
    let out = k ? base.filter((r) => cols.some((c) => String(c.value(r) ?? "").toLowerCase().includes(k))) : base;
    if (sort) {
      const col = cols.find((c) => c.key === sort.key);
      if (col) {
        out = [...out].sort((a, b) => {
          const va = col.value(a);
          const vb = col.value(b);
          if (va === vb) return 0;
          if (va === null || va === "") return 1; // 빈 값은 늘 뒤로
          if (vb === null || vb === "") return -1;
          const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb), "ko");
          return sort.desc ? -cmp : cmp;
        });
      }
    }
    return out;
  }, [base, q, sort, cols]);

  const vehicleCount =
    tab.kind === "vehicles" ? base.filter((r) => !(r as unknown as AnalysisVehicleRow).unlinked).length : 0;
  const unlinked = tab.kind === "vehicles" ? base.find((r) => (r as unknown as AnalysisVehicleRow).unlinked) : undefined;
  // 운행 없이 이벤트만 있는 차량도 들어 있을 때, 화면의 '운행 대수'와 맞춰 볼 수 있게 따로 적는다
  const withTrips =
    tab.kind === "vehicles"
      ? base.filter((r) => !(r as unknown as AnalysisVehicleRow).unlinked && n(r.trips as number) > 0).length
      : 0;
  const total = list.data?.total ?? 0;
  const loaded = list.data?.rows.length ?? 0;

  const countText = !list.data
    ? ""
    : tab.kind === "vehicles"
    ? `차량 ${fmtInt(vehicleCount)}대${withTrips !== vehicleCount ? ` (운행 있는 차량 ${fmtInt(withTrips)}대)` : ""}${
        unlinked ? ` + 차량 미연결 운행 ${fmtInt(unlinked.trips as number)}건` : ""
      }`
    : tab.kind === "devices"
    ? `단말 ${fmtInt(base.length)}대`
    : total > loaded
    ? `최근 ${fmtInt(loaded)}건 (전체 ${fmtInt(total)}건)`
    : `${fmtInt(total)}건`;

  const download = () => {
    const aoa = [cols.map((c) => c.label), ...rows.map((r) => cols.map((c) => c.value(r) ?? ""))];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = cols.map((c) => ({ wch: c.wrap ? 36 : c.label.includes("일시") || c.key.endsWith("_dt") ? 20 : 14 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tab.label.slice(0, 31).replace(/[\\/:*?[\]]/g, " ") || "목록");
    const name = `${title} ${tab.label}`.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_");
    XLSX.writeFile(wb, `${name}_${filter.startDate}_${filter.endDate}.xlsx`);
  };

  const onSort = (key: string) =>
    setSort((s) => (s && s.key === key ? { key, desc: !s.desc } : { key, desc: true }));

  return (
    <>
      <Toolbar>
        <span className="count">
          {filter.startDate} ~ {filter.endDate}
          {filter.keyword ? ` · 검색 '${filter.keyword}'` : ""} · {countText}
          {q.trim() && list.data ? ` 중 ${fmtInt(rows.length)}줄` : ""}
        </span>
        <input type="search" placeholder="목록 안에서 찾기" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="spacer" />
        <button type="button" className="excel" disabled={!rows.length} onClick={download}>
          엑셀 다운로드
        </button>
      </Toolbar>

      {list.isLoading && <p className="note">목록을 불러오는 중…</p>}
      {list.error && <p className="note bad">불러오지 못했다 — {readShopZoneError(list.error).message}</p>}
      {list.data && !rows.length && <p className="note">해당하는 건이 없다.</p>}

      {rows.length > 0 && (
        <TableWrap>
          <table>
            <thead>
              <tr>
                {tab.kind === "events" && <th>원시 데이터</th>}
                {cols.map((c) => (
                  <th key={c.key} onClick={() => onSort(c.key)} className="sortable" title="눌러서 정렬">
                    {c.label === "처리 시간(분)" ? "처리 시간" : c.label}
                    {sort?.key === c.key ? (sort.desc ? " ▼" : " ▲") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${str(r.id ?? r.vhcl_id)}-${i}`} className={(r as unknown as AnalysisVehicleRow).unlinked ? "unlinked" : undefined}>
                  {tab.kind === "events" && (
                    <td>
                      <button type="button" className="raw" onClick={() => onOpenRaw(Number(r.id))}>
                        보기
                      </button>
                    </td>
                  )}
                  {cols.map((c) => (
                    <td key={c.key} className={[c.align ?? "c", c.wrap === "s" ? "wrap-s" : c.wrap ? "wrap" : ""].join(" ")}>
                      {c.render ? c.render(r) : str(c.value(r)) || "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
      {list.data && total > loaded && (
        <p className="note">
          화면에는 최근 {fmtInt(loaded)}건만 나온다. 기간을 줄이거나 조건을 더 걸어 조회한다.
        </p>
      )}
      {unlinked && (
        <p className="note">
          '차량 미연결 운행'은 단말이 차량에 연결되지 않은 채 쌓인 운행이다. 운행 건수에는 들어가고 운행 대수에는 들어가지 않는다.
        </p>
      )}
    </>
  );
}

interface Props {
  filter: AnalysisFilter;
  /** null 이면 닫힘 */
  spec: DetailSpec | null;
  typeName: (code: number | null) => string;
  onClose: () => void;
  onOpenRaw: (id: number) => void;
}

export default function DetailModal({ filter, spec, typeName, onClose, onOpenRaw }: Props) {
  const { width } = useWindowSize();
  const isMobile = width <= md;
  const [tabIdx, setTabIdx] = useState(0);
  useEffect(() => setTabIdx(0), [spec]);
  const tab = spec?.tabs[Math.min(tabIdx, spec.tabs.length - 1)];

  return (
    <ModalLayout open={spec !== null} onClose={onClose} width="1440px" title={spec?.title ?? ""} pt="60px" pb="24px" fullScreen={isMobile}>
      <Body>
        {spec?.note && (
          <p className="lead">
            {spec.color && <i style={{ background: spec.color }} />}
            {spec.note}
          </p>
        )}
        {spec && spec.tabs.length > 1 && (
          <Tabs role="tablist">
            {spec.tabs.map((t, i) => (
              <button key={t.label} type="button" role="tab" aria-selected={i === tabIdx} onClick={() => setTabIdx(i)}>
                {t.label}
              </button>
            ))}
          </Tabs>
        )}
        {spec && tab && (
          <TabBody
            key={`${spec.title}-${tabIdx}`}
            filter={filter}
            tab={tab}
            title={spec.title}
            typeName={typeName}
            onOpenRaw={onOpenRaw}
          />
        )}
      </Body>
    </ModalLayout>
  );
}

// 주소·해제 사유까지 한 화면에 들어오도록 넓게 쓴다 (화면이 좁으면 화면 폭에 맞춘다)
const Body = styled.div`
  width: min(1440px, calc(100vw - 112px));
  padding: 0 4px 4px;
  font-size: 13px;
  color: ${theme.color.neutral_800};
  .lead {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 10px;
    font-size: 12.5px;
    color: ${theme.color.neutral_500};
  }
  .lead i {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
  }
  .note {
    margin: 8px 0;
    font-size: 12px;
    color: ${theme.color.neutral_500};
  }
  .note.bad {
    color: ${theme.color.red_600};
  }
  @media (max-width: ${md}px) {
    width: auto;
    padding: 0 12px 12px;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 10px;
  border-bottom: 1px solid ${theme.color.neutral_200};
  flex-wrap: wrap;
  button {
    font-size: 13px;
    padding: 7px 14px;
    border: 0;
    border-bottom: 2px solid transparent;
    background: transparent;
    color: ${theme.color.neutral_500};
    cursor: pointer;
    margin-bottom: -1px;
  }
  button[aria-selected="true"] {
    color: ${theme.color.green_500};
    border-bottom-color: ${theme.color.green_500};
    font-weight: 700;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
  .count {
    font-size: 13px;
    font-weight: 600;
  }
  input[type="search"] {
    height: 30px;
    min-width: 200px;
    border: 1px solid ${theme.color.neutral_300};
    border-radius: 6px;
    padding: 0 8px;
    font-size: 12.5px;
  }
  input[type="search"]:focus {
    outline: none;
    border-color: ${theme.color.green_500};
  }
  .spacer {
    flex: 1 1 auto;
  }
  .excel {
    font-size: 12px;
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid ${theme.color.green_500};
    background: ${theme.color.green_500};
    color: #fff;
    cursor: pointer;
  }
  .excel:disabled {
    opacity: 0.4;
    cursor: default;
  }
  @media (max-width: ${md}px) {
    input[type="search"] {
      min-width: 0;
      flex: 1 1 160px;
    }
  }
`;

const TableWrap = styled.div`
  max-height: min(560px, calc(100vh - 260px));
  overflow: auto;
  border: 1px solid ${theme.color.neutral_100};
  border-radius: 8px;
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  /* 머리글은 길면 두 줄로 접어 열 폭을 줄인다 */
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f3f4f6;
    font-weight: 600;
    padding: 7px 6px;
    border-bottom: 1px solid ${theme.color.neutral_200};
    white-space: normal;
    line-height: 1.3;
    word-break: keep-all;
  }
  th.sortable {
    cursor: pointer;
    user-select: none;
  }
  th.sortable:hover {
    color: ${theme.color.green_500};
  }
  td {
    padding: 6px 6px;
    border-bottom: 1px solid #f0f1f3;
    text-align: center;
  }
  td.l {
    text-align: left;
  }
  td.r {
    text-align: right;
  }
  td.wrap {
    white-space: normal;
    min-width: 110px;
    max-width: 260px;
    word-break: keep-all;
  }
  td.wrap-s {
    white-space: normal;
    min-width: 90px;
    max-width: 160px;
    word-break: keep-all;
  }
  td .dt2 {
    display: inline-block;
    line-height: 1.35;
  }
  td .dt2 .tm {
    color: ${theme.color.neutral_500};
  }
  td .warn {
    color: ${theme.color.red_500};
  }
  tbody tr:hover td {
    background: #f9fafb;
  }
  tr.unlinked td {
    color: ${theme.color.neutral_500};
    background: #fafafa;
  }
  .raw {
    font-size: 12px;
    padding: 3px 10px;
    border-radius: 4px;
    border: 1px solid ${theme.color.green_500};
    background: #fff;
    color: ${theme.color.green_500};
    cursor: pointer;
  }
  .raw:hover {
    background: ${theme.color.green_500};
    color: #fff;
  }
`;
